import { observer } from 'mobx-react-lite'
import { EditPencil, Plus as PlusIcon, Trash } from 'iconoir-react'
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import { useSearchParams } from 'react-router-dom'
import { debounce } from 'lodash'
import { ReactElement, useEffect, useRef, useState } from 'react'
import { Button } from 'app/components/ui/button'
import { Input } from 'app/components/ui/input'
import { PageScroll } from 'app/components/layout/PageScroll'
import { TableBody, TableCell, TableRow } from 'app/components/ui/table'
import {
  GROUPED_TABLE_CELL,
  GroupedTable,
  GroupedTableHeaderCard,
  GroupedTableSection,
  type GroupedTableColumn,
} from 'app/components/table/GroupedTable'
import { TooltipWrapper } from 'app/components/ui/tooltip-wrapper'
import { cn } from 'app/lib/utils'
import { useHandleSearchParams } from 'app/utils'
import { ChannelMappingType } from 'lib/channelDefaults'
import type { GroupedChannelMappings } from './useChannelMappings'

export type ChannelMappingsShadcnComponentProps = {
  canEditMapping: boolean
  channelUpdate: string | undefined
  groupedChannelMappings: GroupedChannelMappings
  defaultChannels: string[]
  hasData: boolean
  onAdd: () => void
  onEdit: (channel: ChannelMappingType) => void
  onDelete: (id: string, channel: string) => Promise<void>
  isDefaultMapping: (mapping: ChannelMappingType) => boolean
}

function ChannelSearch() {
  const [searchParams] = useSearchParams()
  const handleSearchParams = useHandleSearchParams()
  const [searchValue, setSearchValue] = useState(searchParams.get(`query`) ?? ``)
  const input = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setSearchValue(searchParams.get(`query`) ?? ``)
  }, [searchParams])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.value?.length && e?.target?.value?.length < 3) return
    handleSearchParams({ query: e.target.value })
  }

  return (
    <div className="relative w-[250px]">
      <MagnifyingGlassIcon className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle" />
      <Input
        ref={input}
        type="search"
        placeholder="Search"
        defaultValue={searchValue}
        onChange={debounce(handleSearchChange, 250)}
        autoComplete="off"
        className="pl-8 text-sm"
      />
    </div>
  )
}

function channelColumns(canEditMapping: boolean): GroupedTableColumn[] {
  return [
    { label: `Source` },
    { label: `Medium` },
    ...(canEditMapping
      ? [{ label: `Actions`, colClassName: `w-[100px]`, headClassName: `text-right` } as GroupedTableColumn]
      : []),
  ]
}

function ActionsCell({
  mapping,
  isDefault,
  onEdit,
  onDelete,
}: {
  mapping: ChannelMappingType
  isDefault: boolean
  onEdit: (channel: ChannelMappingType) => void
  onDelete: (id: string, channel: string) => Promise<void>
}) {
  return (
    <div className="flex justify-end gap-2">
      {isDefault ? (
        <TooltipWrapper message="System mapping cannot be updated">
          <EditPencil className="h-5 w-5 text-minimal" />
        </TooltipWrapper>
      ) : (
        <TooltipWrapper message="Update mapping">
          <EditPencil
            className="h-5 w-5 cursor-pointer text-default hover:text-strong"
            onClick={() => onEdit(mapping)}
          />
        </TooltipWrapper>
      )}
      {isDefault ? (
        <TooltipWrapper message="System mapping cannot be deleted">
          <Trash className="h-5 w-5 text-minimal" />
        </TooltipWrapper>
      ) : (
        <TooltipWrapper message="Delete mapping">
          <Trash
            className="h-5 w-5 cursor-pointer text-default hover:text-red-600"
            onClick={() => onDelete(mapping.id!, mapping.channel!)}
          />
        </TooltipWrapper>
      )}
    </div>
  )
}

export const ChannelMappingsShadcnComponent = observer(function ChannelMappingsShadcnComponent({
  canEditMapping,
  channelUpdate,
  groupedChannelMappings,
  defaultChannels,
  hasData,
  onAdd,
  onEdit,
  onDelete,
  isDefaultMapping,
}: ChannelMappingsShadcnComponentProps): ReactElement {
  return (
    // Top padding lives on the content, not the scroller: padding on the scroll
    // container would inset the sticky header card and let rows peek above it.
    <PageScroll contentClassName="pb-6">
      <div className="mb-0.5 flex items-center gap-x-2 pt-6">
        <ChannelSearch />
        {canEditMapping && (
          <Button variant="primary" size="sm" onClick={onAdd}>
            <PlusIcon className="h-4 w-4" />
            <span>New Mapping</span>
          </Button>
        )}
      </div>
      <div className="mt-6 flex flex-col gap-5">
        <GroupedTableHeaderCard columns={channelColumns(canEditMapping)} />
        {defaultChannels.map((dc) => {
          const mappings = groupedChannelMappings[dc] ?? []
          const loading = channelUpdate === dc
          return (
            <GroupedTableSection key={dc} label={dc} count={mappings.length}>
              <GroupedTable columns={channelColumns(canEditMapping)}>
                <TableBody className="divide-y divide-subtle">
                  {loading ? (
                    <TableRow>
                      <TableCell
                        colSpan={canEditMapping ? 3 : 2}
                        className={cn(GROUPED_TABLE_CELL, `text-center text-subtle`)}
                      >
                        Loading…
                      </TableCell>
                    </TableRow>
                  ) : mappings.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={canEditMapping ? 3 : 2}
                        className={cn(GROUPED_TABLE_CELL, `text-center text-subtle`)}
                      >
                        No mappings
                      </TableCell>
                    </TableRow>
                  ) : (
                    mappings.map((m, i) => (
                      <TableRow key={m.id ?? `${m.source}-${m.medium}-${i}`}>
                        <TableCell className={cn(GROUPED_TABLE_CELL, `text-strong`)}>{m.source}</TableCell>
                        <TableCell className={cn(GROUPED_TABLE_CELL, `text-default`)}>{m.medium}</TableCell>
                        {canEditMapping && (
                          <TableCell className={GROUPED_TABLE_CELL}>
                            <ActionsCell
                              mapping={m}
                              isDefault={isDefaultMapping(m)}
                              onEdit={onEdit}
                              onDelete={onDelete}
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </GroupedTable>
            </GroupedTableSection>
          )
        })}
        {!hasData && <div className="py-5 text-center text-subtle">No data found</div>}
      </div>
    </PageScroll>
  )
})
